/**
 * This file combines all category flag objects into a single const object.
 * TypeScript enforces type safety automatically - if a category file is missing
 * or has incorrect types, the build will fail.
 *
 * This approach uses TypeScript's type system instead of a script, but requires
 * manually importing all category files. However, TypeScript will catch any
 * missing imports or type errors at compile time.
 *
 * Duplicate detection:
 * - Runtime: The validateUniqueKeys function will throw an error at module load
 *   time if duplicate TypeScript property names (keys) or duplicate LaunchDarkly
 *   flag values are found across category objects. This ensures that duplicates
 *   are caught immediately when the module is imported.
 */

import { ObjectValues } from 'type-alias';
import { SpaceOpsFlags } from './categories/space-ops';
import { ActivateCoreFlags } from './categories/activate-core';
import { ActivateExperienceIntegrationsFlags } from './categories/activate-experience-integrations';
import { ActivateMobileCoreFlags } from './categories/activate-mobile-core';
import { AccessManagementFlags } from './categories/access-management';
import { TechnicalSolutionsFlags } from './categories/technical-solutions';

type CategoryInfo = {
  name: string;
  obj: Record<string, string>;
};

type DuplicateKey = {
  key: string;
  categories: string[];
};

type DuplicateValue = {
  value: string;
  keys: Array<{ key: string; category: string }>;
};

function findDuplicates<T>(
  map: Map<string, T[]>
): Array<{ key: string; items: T[] }> {
  const duplicates: Array<{ key: string; items: T[] }> = [];

  for (const [key, items] of map.entries()) {
    if (items.length > 1) {
      duplicates.push({ key, items });
    }
  }

  return duplicates;
}

function collectDuplicates(allCategories: readonly CategoryInfo[]): {
  duplicateKeys: DuplicateKey[];
  duplicateValues: DuplicateValue[];
} {
  const keyToCategories = new Map<string, string[]>();
  const valueToKeys = new Map<
    string,
    Array<{ key: string; category: string }>
  >();

  for (const category of allCategories) {
    for (const [key, flagValue] of Object.entries(category.obj)) {
      // Check for duplicate TypeScript property names (keys)
      const existing = keyToCategories.get(key);

      if (existing) {
        existing.push(category.name);
      } else {
        keyToCategories.set(key, [category.name]);
      }

      // Check for duplicate LaunchDarkly flag values
      const existingValue = valueToKeys.get(flagValue);

      if (existingValue) {
        existingValue.push({ key, category: category.name });
      } else {
        valueToKeys.set(flagValue, [{ key, category: category.name }]);
      }
    }
  }

  const keyDuplicates = findDuplicates(keyToCategories);
  const valueDuplicates = findDuplicates(valueToKeys);

  const duplicateKeys: DuplicateKey[] = keyDuplicates.map(({ key, items }) => ({
    key,
    categories: items,
  }));

  const duplicateValues: DuplicateValue[] = valueDuplicates.map(
    ({ key, items }) => ({
      value: key,
      keys: items,
    })
  );

  return { duplicateKeys, duplicateValues };
}

function formatErrorMessage(
  duplicateKeys: DuplicateKey[],
  duplicateValues: DuplicateValue[]
): string {
  const errors: string[] = [];

  if (duplicateKeys.length > 0) {
    errors.push(
      `Duplicate feature flag keys detected:\n${duplicateKeys
        .map(
          ({ key, categories }) =>
            `  - "${key}" found in: ${categories.join(', ')}`
        )
        .join('\n')}`
    );
  }

  if (duplicateValues.length > 0) {
    errors.push(
      `Duplicate LaunchDarkly flag values detected:\n${duplicateValues
        .map(
          ({ value, keys }) =>
            `  - "${value}" used by: ${keys.map(k => `${k.category}.${k.key}`).join(', ')}`
        )
        .join('\n')}`
    );
  }

  return `${errors.join('\n\n')}\n\nPlease ensure all flag keys and LaunchDarkly flag values are unique across all category files.`;
}

/**
 * Runtime validation function to detect duplicate keys and LaunchDarkly flag values across category objects.
 * This will throw an error at module load time if duplicates are found.
 *
 * @throws {Error} If duplicate keys or LaunchDarkly flag values are detected across category objects
 */
function validateUniqueKeys(): void {
  const allCategories: readonly CategoryInfo[] = [
    { name: 'SpaceOpsFlags', obj: SpaceOpsFlags },
    { name: 'ActivateCoreFlags', obj: ActivateCoreFlags },
    {
      name: 'ActivateExperienceIntegrationsFlags',
      obj: ActivateExperienceIntegrationsFlags,
    },
    { name: 'ActivateMobileCoreFlags', obj: ActivateMobileCoreFlags },
    { name: 'AccessManagementFlags', obj: AccessManagementFlags },
    { name: 'TechnicalSolutionsFlags', obj: TechnicalSolutionsFlags },
  ] as const;

  const { duplicateKeys, duplicateValues } = collectDuplicates(allCategories);

  if (duplicateKeys.length > 0 || duplicateValues.length > 0) {
    throw new Error(formatErrorMessage(duplicateKeys, duplicateValues));
  }
}

/**
 * Combined const object of all feature flags.
 * TypeScript automatically infers the correct types from all category files.
 *
 * Runtime validation ensures no duplicate keys or LaunchDarkly flag values exist across categories.
 */
export const FeatureFlag = {
  // current teams
  ...ActivateExperienceIntegrationsFlags,
  ...ActivateCoreFlags,
  ...ActivateMobileCoreFlags,
  ...AccessManagementFlags,
  ...TechnicalSolutionsFlags,
  ...SpaceOpsFlags,
} as const;

// Validate uniqueness at module load time
validateUniqueKeys();

export type FeatureFlagValues = ObjectValues<typeof FeatureFlag>;
